/**
 * Input Sanitization Utilities
 * 
 * Protects against XSS, SQL injection, and other input-based attacks
 */

/**
 * Sanitize HTML by removing dangerous tags and attributes
 * Uses a whitelist approach
 */
export function sanitizeHtml(input: string): string {
  if (!input) return '';
  
  // Remove script tags and their content
  let sanitized = input.replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '');
  
  // Remove event handlers (onclick, onerror, etc.)
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*["'][^"']*["']/gi, '');
  sanitized = sanitized.replace(/\s*on\w+\s*=\s*[^\s>]*/gi, '');
  
  // Remove javascript: protocol
  sanitized = sanitized.replace(/javascript:/gi, '');
  
  // Remove data: protocol (can be used for XSS)
  sanitized = sanitized.replace(/data:text\/html/gi, '');
  
  // Remove iframe, embed, object tags
  sanitized = sanitized.replace(/<(iframe|embed|object)[^>]*>.*?<\/\1>/gi, '');
  
  return sanitized.trim();
}

/**
 * Escape HTML special characters
 * Converts characters to HTML entities
 */
export function escapeHtml(input: string): string {
  if (!input) return '';
  
  const htmlEscapeMap: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#x27;',
    '/': '&#x2F;',
  };
  
  return input.replace(/[&<>"'/]/g, (char) => htmlEscapeMap[char] || char);
}

/**
 * Sanitize text input for safe display
 * Removes or escapes potentially dangerous characters
 */
export function sanitizeText(input: string): string {
  if (!input) return '';
  
  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');
  
  // Normalize whitespace
  sanitized = sanitized.replace(/\s+/g, ' ').trim();
  
  // Remove control characters except newlines and tabs
  sanitized = sanitized.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
  
  return sanitized;
}

/**
 * Validate and sanitize email
 * Returns null if invalid
 */
export function sanitizeEmail(email: string): string | null {
  if (!email) return null;
  
  // Remove whitespace
  const cleaned = email.trim().toLowerCase();
  
  // Basic email validation
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(cleaned)) {
    return null;
  }
  
  // Check for dangerous characters
  if (/[<>()[\]\\,;:]/.test(cleaned)) {
    return null;
  }
  
  return cleaned;
}

/**
 * Sanitize URL
 * Only allows http, https, and mailto protocols
 */
export function sanitizeUrl(url: string): string | null {
  if (!url) return null;
  
  const cleaned = url.trim();
  
  // Allow only safe protocols
  const safeProtocols = ['http:', 'https:', 'mailto:'];
  
  try {
    const urlObj = new URL(cleaned);
    
    if (!safeProtocols.includes(urlObj.protocol)) {
      return null;
    }
    
    return urlObj.href;
  } catch {
    // If not a valid URL, check if it's a relative path
    if (cleaned.startsWith('/') && !cleaned.startsWith('//')) {
      // Remove any dangerous characters from path
      return cleaned.replace(/[<>]/g, '');
    }
    
    return null;
  }
}

/**
 * Sanitize filename
 * Removes path traversal attempts and dangerous characters
 */
export function sanitizeFilename(filename: string): string {
  if (!filename) return '';
  
  // Remove path components
  let sanitized = filename.replace(/^.*[\\\/]/, '');
  
  // Remove dangerous characters
  sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '_');
  
  // Remove leading dots (hidden files)
  sanitized = sanitized.replace(/^\.+/, '');
  
  // Limit length
  if (sanitized.length > 255) {
    const ext = sanitized.split('.').pop() || '';
    const name = sanitized.substring(0, 255 - ext.length - 1);
    sanitized = `${name}.${ext}`;
  }
  
  return sanitized || 'file';
}

/**
 * Sanitize phone number
 * Keeps only digits and common separators
 */
export function sanitizePhone(phone: string): string {
  if (!phone) return '';
  
  // Keep only digits, spaces, hyphens, parentheses, and plus
  return phone.replace(/[^0-9\s\-()+ ]/g, '').trim();
}

/**
 * Sanitize search query
 * Removes SQL injection attempts and special characters
 */
export function sanitizeSearchQuery(query: string): string {
  if (!query) return '';
  
  let sanitized = query.trim();
  
  // Remove SQL keywords (basic protection)
  const sqlKeywords = [
    'SELECT', 'INSERT', 'UPDATE', 'DELETE', 'DROP', 'CREATE', 
    'ALTER', 'EXEC', 'EXECUTE', 'UNION', 'DECLARE', '--', ';'
  ];
  
  sqlKeywords.forEach(keyword => {
    const regex = new RegExp(`\\b${keyword}\\b`, 'gi');
    sanitized = sanitized.replace(regex, '');
  });
  
  // Remove special characters that could be used in SQL injection
  sanitized = sanitized.replace(/[';"\\\x00]/g, '');
  
  // Limit length
  if (sanitized.length > 200) {
    sanitized = sanitized.substring(0, 200);
  }
  
  return sanitized.trim();
}

/**
 * Deep sanitize an object
 * Recursively sanitizes all string values
 */
export function sanitizeObject<T extends Record<string, any>>(
  obj: T,
  sanitizeFn: (value: string) => string = sanitizeText
): T {
  if (!obj || typeof obj !== 'object') return obj;
  
  const sanitized: any = Array.isArray(obj) ? [] : {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeFn(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value, sanitizeFn);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
}

/**
 * Validate input length
 */
export function validateLength(
  input: string,
  min: number,
  max: number
): { isValid: boolean; error?: string } {
  const length = input.length;
  
  if (length < min) {
    return {
      isValid: false,
      error: `Minimum length is ${min} characters`,
    };
  }
  
  if (length > max) {
    return {
      isValid: false,
      error: `Maximum length is ${max} characters`,
    };
  }
  
  return { isValid: true };
}

/**
 * Check for common XSS patterns
 */
export function containsXss(input: string): boolean {
  if (!input) return false;
  
  const xssPatterns = [
    /<script/i,
    /javascript:/i,
    /on\w+\s*=/i,
    /<iframe/i,
    /<embed/i,
    /<object/i,
    /eval\(/i,
    /expression\(/i,
  ];
  
  return xssPatterns.some(pattern => pattern.test(input));
}
